import {PathUtility} from 'src/shared/constants/PathBuilder';

import type {RiskAssessmentCodeResponse} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';

import {SidebarLinkRiskAssessment} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useRiskAssessmentCodeColumns} from 'src/app/risk-assessment-code/RiskAssessmentCodeTable/hooks/useRiskAssessmentCodeColumns';
import {racDocumentTitle} from 'src/app/risk-assessment-code/riskAssessmentHelpers';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetRiskAssessmentCodeListV2Query} from 'src/redux/queries/riskAsessmentCode';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const RiskAssessmentCodeListPage = () => {
  useSetDocumentTitle(racDocumentTitle);
  const navigate = useAppNavigate();

  const columns = useRiskAssessmentCodeColumns();

  const tableState = useTableState({
    identifier: 'risk-assessment-code-list-page',
    columns,
    defaultColumnVisibility: {},
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetRiskAssessmentCodeListV2Query(
      {
        start,
        length,
        filters,
        filterFns,
        sorting,
        includeDemonstrationGenerated: false,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleAddNew = () => navigate(PathUtility.RiskAssessmentCreate());

  const handleNavigateTo = (row: RiskAssessmentCodeResponse) =>
    navigate(PathUtility.RiskAssessmentView(row.id, 'content'));

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns,
      data: rows,
      getRowId: row => String(row.id),

      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() => handleNavigateTo(row.original)}
          />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <PermissionGuard
              module={ModuleEnum.RiskAssessmentCodes}
              operation={PermissionOperationEnum.Create}>
              <TableToolbar.Action variant="create" onClick={handleAddNew}>
                New Risk Assessment Code
              </TableToolbar.Action>
            </PermissionGuard>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <ContentWrapper>
      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkRiskAssessment.icon}
          subtitle="Manage risk assessment codes">
          Risk Assessment Codes
        </PageHeader.Title>
      </PageHeader>
      {table}
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.RiskAssessment(),
  element: <RiskAssessmentCodeListPage />,
  module: ModuleEnum.RiskAssessmentCodes,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

export default RiskAssessmentCodeListPage;
